/**
 * Listen, Connect, and Help — survey-based flow for a disciple.
 * Subscribe to a survey → Send question (Listen) → Add notes (Connect) → Share verse (Help) → next question.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import { toast } from 'react-hot-toast';
import {
  ChatBubbleBottomCenterTextIcon,
  CheckIcon,
  DocumentTextIcon,
  HeartIcon,
  BookOpenIcon,
  UserCircleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import DiscipleNotes from '../components/DiscipleNotes';
import { getSuggestedVerses } from '../utils/verseAlgorithm';

const STEP_LABELS = { listen: 'Listen', connect: 'Connect', help: 'Help' };
const STEPS = ['listen', 'connect', 'help'];

const ListenConnectHelp = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [disciple, setDisciple] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [userAppID, setUserAppID] = useState('');
  const [updating, setUpdating] = useState(false);
  const [viewStep, setViewStep] = useState(() => location.state?.step === 'help' ? 'help' : null);
  const [listenPersonalMessage, setListenPersonalMessage] = useState('');
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [contentTrackHelpInfo, setContentTrackHelpInfo] = useState(null);
  const [showListenTip, setShowListenTip] = useState(false);
  const [topSuggestedVerse, setTopSuggestedVerse] = useState(null);

  const subscribedSurveyId = mentorDetails?.subscribedSurveyId ?? null;
  const subscribedContentTrackId = mentorDetails?.subscribedContentTrackId ?? null;
  const progress = mentorDetails?.surveyProgress ?? { currentQuestionIndex: 0, step: 'listen' };
  const currentStep = progress.step || 'listen';
  const currentIndex = progress.currentQuestionIndex ?? 0;
  const activeStep = viewStep ?? (subscribedSurveyId ? currentStep : 'listen');
  const nextQuestion = surveyQuestions[currentIndex];
  const hasNextSurveyQuestion = currentIndex + 1 < surveyQuestions.length;
  const MY_OWN_QUESTION_INDEX = -1;
  const displayQuestionIndex =
    selectedQuestionIndex !== null && selectedQuestionIndex !== undefined
      ? selectedQuestionIndex
      : currentIndex;
  const isMyOwnQuestion = displayQuestionIndex === MY_OWN_QUESTION_INDEX;
  const displayQuestion = isMyOwnQuestion ? null : surveyQuestions[displayQuestionIndex];
  const isSendingNextInSequence = displayQuestionIndex === currentIndex && currentIndex < surveyQuestions.length;

  const loadData = async () => {
    if (!discipleId || !currentUser) return;
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const appID = userDoc.exists() ? userDoc.data().appID : null;
      if (!appID) {
        toast.error('User appID not found.');
        setLoading(false);
        return;
      }
      setUserAppID(appID);

      const [discipleSnap, mentorSnap, surveysSnap] = await Promise.all([
        getDoc(doc(db, 'disciples', discipleId)),
        getDoc(doc(db, 'disciples', discipleId, 'mentorDetails', appID)),
        getDocs(collection(db, 'surveys')),
      ]);

      if (discipleSnap.exists()) {
        setDisciple({ id: discipleSnap.id, ...discipleSnap.data() });
      } else {
        setDisciple(null);
      }
      if (mentorSnap.exists()) {
        setMentorDetails(mentorSnap.data());
      } else {
        setMentorDetails(null);
      }
      setSurveys(surveysSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading ListenConnectHelp data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!discipleId || !currentUser) return;
    loadData();
  }, [discipleId, currentUser]);

  useEffect(() => {
    if (!subscribedSurveyId) return;
    loadSurveyAndQuestions(subscribedSurveyId);
  }, [subscribedSurveyId]);

  useEffect(() => {
    if (!subscribedContentTrackId || !userAppID) {
      setContentTrackHelpInfo(null);
      return;
    }
    let cancelled = false;
    const loadContentTrackHelp = async () => {
      try {
        const [trackSnap, resourcesSnap] = await Promise.all([
          getDoc(doc(db, 'contentTracks', subscribedContentTrackId)),
          getDocs(collection(db, 'contentTracks', subscribedContentTrackId, 'resources')),
        ]);
        if (cancelled) return;
        const trackTitle = trackSnap.exists() ? (trackSnap.data().name || 'Content track') : 'Content track';
        const resources = resourcesSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => r.status === 'published' || !r.status)
          .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        const total = resources.length;
        const sentIds = mentorDetails?.contentTrackProgress?.sentResourceIds || [];
        const nextResource = resources.find((r) => !sentIds.includes(r.id));
        const sentCount = sentIds.length;
        const currentIndex = nextResource ? sentCount + 1 : total;
        setContentTrackHelpInfo({
          trackTitle,
          nextResourceTitle: nextResource?.title || null,
          currentIndex: total > 0 ? currentIndex : 0,
          totalCount: total,
        });
      } catch (err) {
        if (!cancelled) setContentTrackHelpInfo(null);
      }
    };
    loadContentTrackHelp();
    return () => { cancelled = true; };
  }, [subscribedContentTrackId, userAppID, mentorDetails?.contentTrackProgress?.sentCount]);

  // Load top suggested verse for Help section microcopy (same algorithm as Suggested Verses page)
  useEffect(() => {
    if (!discipleId || !userAppID) {
      setTopSuggestedVerse(null);
      return;
    }
    let cancelled = false;
    const loadTopSuggestedVerse = async () => {
      try {
        const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
        const [versesSnap, notesSnap, messagesSnap, messagesSMSSnap] = await Promise.all([
          getDocs(collection(db, 'verses')),
          getDocs(collection(mentorRef, 'notes')),
          getDocs(collection(mentorRef, 'messages')),
          getDocs(collection(mentorRef, 'messagesSMS')),
        ]);
        if (cancelled) return;
        const allVerses = versesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const notes = notesSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((n) => !n.archived && n.note !== 'Initial note for disciple')
          .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
          .slice(0, 2);
        const sentRefs = new Set(
          [...messagesSnap.docs, ...messagesSMSSnap.docs]
            .map((d) => d.data().verseReference)
            .filter(Boolean)
        );
        const availableVerses = allVerses.filter((v) => !sentRefs.has(v.reference));
        const mentor = mentorDetails || {};
        const context = {
          ageRange: mentor.faithJourneyAgeRange,
          relationshipStatus: mentor.faithJourneyRelationship,
          discipleshipStage: mentor.faithJourneyStage,
          christianExperience: mentor.faithJourneyExperience,
        };
        const suggestions = getSuggestedVerses({
          context,
          notes,
          availableVerses,
          count: 1,
          discipleId,
        });
        if (!cancelled && suggestions?.length) setTopSuggestedVerse(suggestions[0]);
        else if (!cancelled) setTopSuggestedVerse(null);
      } catch (err) {
        if (!cancelled) setTopSuggestedVerse(null);
      }
    };
    loadTopSuggestedVerse();
    return () => { cancelled = true; };
  }, [discipleId, userAppID, mentorDetails]);

  const loadSurveyAndQuestions = async (surveyIdParam) => {
    const id = surveyIdParam ?? subscribedSurveyId;
    if (!id) return;
    try {
      const surveySnap = await getDoc(doc(db, 'surveys', id));
      if (surveySnap.exists()) setSurvey({ id: surveySnap.id, ...surveySnap.data() });

      const qRef = collection(db, 'surveys', id, 'questions');
      let snap;
      try {
        snap = await getDocs(query(qRef, orderBy('order', 'asc')));
      } catch {
        snap = await getDocs(qRef);
      }
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSurveyQuestions(list);
    } catch (err) {
      console.error('Error loading survey questions:', err);
    }
  };

  const mentorRef = () => doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);

  const subscribeToSurvey = async (surveyId) => {
    if (!userAppID) return;
    setUpdating(true);
    try {
      await setDoc(mentorRef(), {
        subscribedSurveyId: surveyId,
        surveyProgress: { currentQuestionIndex: 0, step: 'listen' },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      const s = surveys.find((x) => x.id === surveyId);
      if (s) setSurvey(s);
      await loadSurveyAndQuestions(surveyId);
      setMentorDetails((prev) => ({
        ...prev,
        subscribedSurveyId: surveyId,
        surveyProgress: { currentQuestionIndex: 0, step: 'listen' },
      }));
      toast.success(`Subscribed to "${s?.name || 'survey'}"`);
    } catch (err) {
      console.error('Error subscribing to survey:', err);
      toast.error('Failed to subscribe');
    } finally {
      setUpdating(false);
    }
  };

  const unsubscribeFromSurvey = async () => {
    if (!window.confirm('Unsubscribe from this survey? Progress will be lost.')) return;
    setUpdating(true);
    try {
      await updateDoc(mentorRef(), {
        subscribedSurveyId: null,
        surveyProgress: null,
        updatedAt: serverTimestamp(),
      });
      setMentorDetails((prev) => ({ ...prev, subscribedSurveyId: null, surveyProgress: null }));
      setSurvey(null);
      setSurveyQuestions([]);
      toast.success('Unsubscribed');
    } catch (err) {
      console.error('Error unsubscribing:', err);
      toast.error('Failed to unsubscribe');
    } finally {
      setUpdating(false);
    }
  };

  const markQuestionSent = async () => {
    setUpdating(true);
    try {
      await updateDoc(mentorRef(), {
        'surveyProgress.step': 'connect',
        'surveyProgress.lastQuestionSentAt': new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      setMentorDetails((prev) => ({
        ...prev,
        surveyProgress: { ...progress, step: 'connect', lastQuestionSentAt: new Date().toISOString() },
      }));
      setSelectedQuestionIndex(null);
      toast.success('Next step: Connect — add notes about what they shared.');
    } catch (err) {
      console.error('Error updating step:', err);
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const chooseSendSurveyQuestion = async () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= surveyQuestions.length) {
      toast.error('No more questions in this survey.');
      return;
    }
    setUpdating(true);
    try {
      await updateDoc(mentorRef(), {
        'surveyProgress.step': 'listen',
        'surveyProgress.currentQuestionIndex': nextIndex,
        updatedAt: serverTimestamp(),
      });
      setMentorDetails((prev) => ({
        ...prev,
        surveyProgress: { ...progress, step: 'listen', currentQuestionIndex: nextIndex },
      }));
      toast.success(`Send question ${nextIndex + 1} of ${surveyQuestions.length}`);
    } catch (err) {
      console.error('Error advancing to next question:', err);
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const markHelpDoneAndAdvance = async () => {
    const nextIndex = currentIndex + 1;
    const isSurveyComplete = nextIndex >= surveyQuestions.length;
    setUpdating(true);
    try {
      if (isSurveyComplete) {
        await updateDoc(mentorRef(), {
          subscribedSurveyId: null,
          surveyProgress: null,
          updatedAt: serverTimestamp(),
        });
        setMentorDetails((prev) => ({ ...prev, subscribedSurveyId: null, surveyProgress: null }));
        setSurvey(null);
        setSurveyQuestions([]);
        toast.success('Survey complete! You can subscribe to another survey or start a new one.');
      } else {
        await updateDoc(mentorRef(), {
          'surveyProgress.step': 'listen',
          'surveyProgress.currentQuestionIndex': nextIndex,
          updatedAt: serverTimestamp(),
        });
        setMentorDetails((prev) => ({
          ...prev,
          surveyProgress: { ...progress, step: 'listen', currentQuestionIndex: nextIndex },
        }));
        toast.success(`Next: Send question ${nextIndex + 1} of ${surveyQuestions.length}`);
      }
    } catch (err) {
      console.error('Error advancing:', err);
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const getListenMessageBody = () => {
    if (isMyOwnQuestion) return listenPersonalMessage?.trim() || '';
    if (!displayQuestion?.questionText) return '';
    const personal = listenPersonalMessage?.trim() || '';
    return personal ? `${personal}\n\n${displayQuestion.questionText}` : displayQuestion.questionText;
  };

  const openSms = () => {
    if (!disciple?.phone) return;
    const body = getListenMessageBody();
    if (!body) return;
    const phone = disciple.phone.replace(/\D/g, '');
    const encoded = encodeURIComponent(body);
    window.open(`sms:${phone.length === 11 && phone.startsWith('1') ? '+' : ''}${phone}?body=${encoded}`, '_blank');
  };

  const lastTwoQuestionsSent = surveyQuestions.filter((_, i) => i < currentIndex).slice(-2);

  if (loading && !disciple) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <p className="text-gray-500">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!disciple) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <p className="text-red-600">Disciple not found.</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-blue-600">Go back</button>
        </div>
      </Layout>
    );
  }

  const photoURL = mentorDetails?.photoURL || disciple?.photoURL;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-24 min-h-[80vh]">
        {/* Row 1: Disciple image (tap → disciple menu) + Listen | Connect | Help */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate(`/disciple-menu/${discipleId}`)}
            className="flex-shrink-0 rounded-full overflow-hidden w-14 h-14 sm:w-16 sm:h-16 border-2 border-white shadow-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label={`Back to ${disciple.name}`}
          >
            {photoURL ? (
              <img src={photoURL} alt={disciple.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <UserCircleIcon className="w-9 h-9 text-gray-400" />
              </div>
            )}
          </button>
          <div className="flex flex-1 items-center min-w-0">
            {STEPS.map((step, i) => (
              <React.Fragment key={step}>
                {i > 0 && (
                  <div
                    className={`flex-1 h-1 min-w-[8px] mx-0.5 rounded-full transition-colors duration-200 ease-out ${
                      activeStep === step || (STEPS.indexOf(activeStep) > i) ? 'bg-[#0076BE]' : 'bg-gray-200'
                    }`}
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  onClick={() => setViewStep(step)}
                  className={`flex-1 flex flex-col items-center gap-0.5 min-w-0 py-1.5 px-1.5 sm:px-2 rounded-lg border-2 transition-all duration-200 ease-out ${
                    activeStep === step
                      ? 'bg-white text-[#0076BE] border-[#0076BE] shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  aria-pressed={activeStep === step}
                  aria-label={`Go to ${STEP_LABELS[step]}`}
                >
                  <span
                    className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full border-2 flex-shrink-0 ${
                      activeStep === step ? 'border-[#0076BE] bg-[#0076BE]' : 'border-gray-400 bg-transparent'
                    }`}
                  />
                  <span className="text-[11px] sm:text-xs font-medium truncate w-full text-center leading-tight">{STEP_LABELS[step]}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Row 2: Content for active step (Listen, Connect, or Help) — min-height reduces footer jank when switching */}
        <div className="min-h-[50vh]">
        {activeStep === 'listen' && (
          <div className="relative bg-white rounded-lg shadow p-6 animate-fade-in-up">
            <button
              type="button"
              onClick={() => setShowListenTip((v) => !v)}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-[#0076BE] hover:bg-gray-100 focus:ring-2 focus:ring-[#0076BE] focus:ring-offset-2"
              aria-label="What we're doing here"
            >
              <InformationCircleIcon className="h-6 w-6" />
            </button>
            {showListenTip && (
              <div className="mb-4 p-4 rounded-xl bg-blue-50 border border-blue-100 text-sm text-gray-700 animate-fade-in">
                <p className="font-medium text-gray-900 mb-1">What we're doing here</p>
                <p>Listen means asking a good question — from a survey or your own — so you can hear where they are. After they reply, you’ll add notes (Connect), then follow up with a verse or content (Help). One question at a time keeps the conversation natural.</p>
              </div>
            )}
            {!subscribedSurveyId ? (
          <>
            {/* Step 1: Pray */}
            <div className="flex gap-3 mb-6">
              <span className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-[#0076BE] bg-white flex items-center justify-center text-sm font-bold text-[#0076BE]">1</span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Pray for {disciple?.name?.split(/\s+/)[0] || 'them'}.</h2>
                <p className="text-sm text-gray-600 mt-0.5">Ask God to make His presence known and to use your conversation to bless them.</p>
              </div>
            </div>
            {/* Step 2: Ask an interesting question — dynamic button "Find Good Questions" */}
            <div className="flex gap-3 mb-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-[#0076BE] bg-white flex items-center justify-center text-sm font-bold text-[#0076BE]">2</span>
              <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Ask an interesting question</h2>
                  <p className="text-sm text-gray-600 mt-0.5">Select a question from here or come up with your own.</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/view-disciple/${discipleId}`)}
                  className="flex-shrink-0 self-start sm:self-center px-4 py-2 rounded-xl text-sm font-medium bg-[#0076BE] text-white hover:opacity-90 transition"
                >
                  Find Good Questions
                </button>
              </div>
            </div>
            <p className="text-gray-600 mb-4">Choose a survey to walk through with this person. You’ll send one question, then connect with notes, then help with a verse — then the next question.</p>
            {surveys.length === 0 ? (
              <p className="text-gray-500">No surveys available. Ask a super admin to create boilerplate surveys.</p>
            ) : (
              <ul className="space-y-2">
                {surveys.map((s) => (
                  <li key={s.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <span className="font-medium text-gray-900">{s.name}</span>
                      {s.description && <p className="text-sm text-gray-500">{s.description}</p>}
                    </div>
                    <button
                      onClick={() => subscribeToSurvey(s.id)}
                      disabled={updating}
                      className="px-4 py-2 bg-[#0076BE] text-white text-sm font-medium rounded-md hover:bg-[#0066a1] disabled:opacity-50"
                    >
                      Subscribe
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
          ) : (
          <>
                {/* Step 1: Pray */}
                <div className="flex gap-3 mb-6">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-[#0076BE] bg-white flex items-center justify-center text-sm font-bold text-[#0076BE]">1</span>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Pray for {disciple?.name?.split(/\s+/)[0] || 'them'}.</h2>
                    <p className="text-sm text-gray-600 mt-0.5">Ask God to make His presence known and to use your conversation to bless them.</p>
                  </div>
                </div>
                {/* Step 2: Ask an interesting question — title and dynamic button on same line */}
                <div className="flex gap-3 mb-2">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-[#0076BE] bg-white flex items-center justify-center text-sm font-bold text-[#0076BE]">2</span>
                  <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Ask an interesting question</h2>
                      <p className="text-sm text-gray-600 mt-0.5">Select a question from here or come up with your own.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/view-disciple/${discipleId}`)}
                      className="flex-shrink-0 self-start sm:self-center px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 transition"
                    >
                      {survey?.name || 'Survey'}
                    </button>
                  </div>
                </div>
                {/* Dropdown directly under microcopy — mobile-style, no duplicate label */}
                {surveyQuestions.length === 0 ? (
                  <p className="text-gray-500 mb-4">No questions in this survey yet. You can unsubscribe or try again later.</p>
                ) : (
                  <>
                    <div className="relative mb-4">
                      <select
                        value={displayQuestionIndex}
                        onChange={(e) => setSelectedQuestionIndex(Number(e.target.value))}
                        className="w-full appearance-none pl-4 pr-12 py-4 text-base text-gray-900 bg-gray-50 border border-gray-400 rounded-2xl shadow-sm focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] focus:bg-white transition cursor-pointer"
                      >
                        <option value={MY_OWN_QUESTION_INDEX}>My Own Question</option>
                        {surveyQuestions.map((q, i) => (
                          <option key={q.id || i} value={i}>
                            {q.questionText || `Question ${i + 1}`}
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <ChevronDownIcon className="h-5 w-5" />
                      </span>
                    </div>
                    {lastTwoQuestionsSent.length > 0 && (
                      <div className="mb-6 p-4 rounded-xl bg-gray-50 border border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Last {lastTwoQuestionsSent.length} question{lastTwoQuestionsSent.length > 1 ? 's' : ''} we sent</h3>
                        <ul className="space-y-3">
                          {lastTwoQuestionsSent.map((q, i) => (
                            <li key={q.id || i} className="text-sm text-gray-600 pl-3 border-l-2 border-gray-200">
                              {q.questionText}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isMyOwnQuestion ? 'Type your own question.' : 'Add a personal message (optional)'}
                      </label>
                      <textarea
                        value={listenPersonalMessage}
                        onChange={(e) => setListenPersonalMessage(e.target.value)}
                        placeholder={isMyOwnQuestion ? "e.g. What's been on your mind lately?" : 'e.g. Hey, been thinking about you — wanted to ask you this...'}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] resize-none"
                      />
                    </div>
                    <div className="mb-6">
                      <p className="text-xs font-medium text-gray-500 mb-2">Message preview</p>
                      <div className="flex justify-end">
                        <div className="max-w-[85%] sm:max-w-md">
                          <div className="relative bg-[#007AFF] text-white px-4 py-3 rounded-2xl rounded-br-md shadow-sm">
                            {getListenMessageBody() ? (
                              <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                                {getListenMessageBody()}
                              </p>
                            ) : (
                              <p className="text-sm text-white/70 italic">Your message will appear here.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 items-center justify-end">
                      {disciple.phone && (
                        <button onClick={openSms} className="px-4 py-2 bg-white border-2 border-green-600 text-green-600 text-sm font-medium rounded-md hover:bg-green-50">
                          Open Messages and Send
                        </button>
                      )}
                    </div>
                    {isSendingNextInSequence && (
                      <p className="mt-3">
                        <button
                          type="button"
                          onClick={markQuestionSent}
                          disabled={updating}
                          className="text-sm text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
                        >
                          I already sent this question
                        </button>
                      </p>
                    )}
                    <p className="mt-4 text-sm text-gray-500">Clicking the Open Messages and Send button will bring the conversation to your messaging app to text with your disciple there.</p>
                  </>
                )}
          </>
          )}
        </div>
        )}

        {activeStep === 'connect' && (
              <div className="bg-white rounded-lg shadow p-6 space-y-8 animate-fade-in-up">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Connect — Discipleship Notes</h2>
                  <p className="text-sm text-gray-600 mb-4">Take a minute to think about your conversation, and write down your thoughts. Pray for them, and then ask the Holy Spirit to guide you as you take a few notes here about where they are in their journey.</p>
                </div>
                {userAppID && (
                  <DiscipleNotes discipleId={discipleId} userAppID={userAppID} />
                )}
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Your journey</h3>
                  <p className="text-gray-600 text-sm mb-3">Your My Journey helps you bring relevant parts of your story into the conversation. Open it here when you want to reference it.</p>
                  {userAppID ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/user/${userAppID}?tab=journey`)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-blue-200 bg-blue-50/50 hover:bg-blue-50 text-[#0076BE] font-medium text-sm transition"
                    >
                      <BookOpenIcon className="h-5 w-5" />
                      View your My Journey
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            {activeStep === 'help' && (
              <div className="bg-white rounded-lg shadow p-6 animate-fade-in-up">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Help — What will you send?</h2>
                <p className="text-sm text-gray-600 mb-4">Choose how you want to follow up. TOM will take you to the right place to send it.</p>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setViewStep('listen')}
                    className="w-full flex items-center gap-4 p-4 text-left rounded-xl border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 transition"
                  >
                    <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-[#0076BE]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-gray-900 block">Ask a creative question</span>
                      <span className="text-sm text-gray-600 block">Go back to Listen to choose or write a question to send.</span>
                      {nextQuestion?.questionText && (
                        <span className="text-sm text-gray-700 mt-1.5 block italic">&ldquo;{nextQuestion.questionText}&rdquo;</span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/suggested-verses/${discipleId}`)}
                    className="w-full flex items-center gap-4 p-4 text-left rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100/80 transition"
                  >
                    <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <HeartIcon className="h-6 w-6 text-amber-600" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-gray-900 block">Send a Bible Verse</span>
                      <span className="text-sm text-gray-600 block">Pick a suggested verse and reflection question to send.</span>
                      {topSuggestedVerse?.verse && (
                        <span className="text-sm text-gray-700 mt-1.5 block">
                          <span className="font-medium">{topSuggestedVerse.verse.reference}</span>
                          {topSuggestedVerse.verse.text && (
                            <span className="text-gray-600"> — {(topSuggestedVerse.verse.text || '').slice(0, 80)}{(topSuggestedVerse.verse.text || '').length > 80 ? '…' : ''}</span>
                          )}
                        </span>
                      )}
                    </div>
                  </button>
                  {subscribedContentTrackId ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/suggested-content/${discipleId}`)}
                      className="w-full flex items-center gap-4 p-4 text-left rounded-xl border-2 border-green-300 bg-green-50 hover:bg-green-100 transition"
                    >
                      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <BookOpenIcon className="h-6 w-6 text-green-600" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-gray-900 block">Send Content</span>
                        <span className="text-sm text-gray-600 block">
                          {contentTrackHelpInfo?.nextResourceTitle
                            ? contentTrackHelpInfo.nextResourceTitle
                            : contentTrackHelpInfo
                              ? 'Next piece from their track'
                              : 'Choose a content track for this disciple.'}
                        </span>
                        {contentTrackHelpInfo?.trackTitle && contentTrackHelpInfo.totalCount > 0 && (
                          <span className="text-sm text-gray-500 block mt-0.5">
                            {contentTrackHelpInfo.trackTitle} — {contentTrackHelpInfo.currentIndex} of {contentTrackHelpInfo.totalCount}
                          </span>
                        )}
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(`/browse-content-tracks/${discipleId}`)}
                      className="w-full flex items-center gap-4 p-4 text-left rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 transition"
                    >
                      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                        <BookOpenIcon className="h-6 w-6 text-gray-400" />
                      </span>
                      <div>
                        <span className="font-semibold text-gray-700 block">Browse Content Tracks</span>
                        <span className="text-sm">Send a resource such as an article or a video.</span>
                      </div>
                    </button>
                  )}
                </div>
                {false && currentIndex + 1 >= surveyQuestions.length && (
                  <p className="mt-2 text-sm text-amber-700">This is the last question. Clicking “I’ve shared — next question” will complete the survey.</p>
                )}
              </div>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default ListenConnectHelp;
